import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import {
    FaSearch,
    FaSyncAlt,
    FaChevronLeft,
    FaChevronRight,
    FaInfoCircle,
    FaCopy,
    FaCheckCircle,
    FaTimesCircle,
    FaClock,
    FaFilter,
    FaEllipsisV,
    FaTimes,
    FaWallet,
} from "react-icons/fa";
import { CgProfile } from "react-icons/cg";
import { useNavigate } from "react-router-dom";

/**
 * WithdrawalList Page
 * - Similar design to DepositList
 * - Adjusted for withdrawal data structure
 * - Shows amount breakdown (amount, charge, net)
 * - Displays wallet information (type and address)
 */

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt(v, digits = 5) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(num);
}

function formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return safeText(iso);
    // Format: DD/MM/YYYY HH:MM
    return d.toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
}

function formatDateShort(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return safeText(iso);
    // Format: DD/MM/YYYY
    return d.toLocaleDateString('en-IN');
}

async function copyToClipboard(text) {
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (_) { }
    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        ta.style.left = "-1000px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
    } catch (_) {
        return false;
    }
}

function CopyBtn({ value, label = "Copied", disabled }) {
    const isDisabled = disabled || value === null || value === undefined || value === "" || value === "—";
    const onCopy = async () => {
        const ok = await copyToClipboard(String(value));
        if (ok) toast.success(label);
        else toast.error("Copy failed");
    };
    return (
        <button
            type="button"
            className="btn btn-sm btn-outline-secondary border-0 p-1"
            onClick={onCopy}
            disabled={isDisabled}
            title="Copy"
        >
            <FaCopy size={12} />
        </button>
    );
}

function LoadingRow({ cols = 8 }) {
    return (
        <tr>
            {Array.from({ length: cols }).map((_, i) => (
                <td key={i}>
                    <div className="placeholder-glow">
                        <span className="placeholder col-12" />
                    </div>
                </td>
            ))}
        </tr>
    );
}

function StatusBadge({ status }) {
    const s = String(status);
    if (s === "0")
        return (
            <span className="badge bg-warning text-dark d-inline-flex align-items-center gap-1 px-2 py-1">
                <FaClock size={10} /> Pending
            </span>
        );
    if (s === "1")
        return (
            <span className="badge bg-success d-inline-flex align-items-center gap-1 px-2 py-1">
                <FaCheckCircle size={10} /> Success
            </span>
        );
    if (s === "2")
        return (
            <span className="badge bg-danger d-inline-flex align-items-center gap-1 px-2 py-1">
                <FaTimesCircle size={10} /> Rejected
            </span>
        );
    return <span className="badge bg-secondary px-2 py-1">Unknown</span>;
}

/* ---------- Pagination ---------- */
function PaginationBar({ loading, pageNo, totalPages, onPrev, onNext, onJump }) {
    const pages = useMemo(() => {
        const n = Number(totalPages || 1);
        return Array.from({ length: n }, (_, i) => i + 1);
    }, [totalPages]);

    return (
        <div className="d-flex justify-content-between align-items-center">
            <div>
                <span className="text-muted small">
                    Page {pageNo} of {totalPages}
                </span>
            </div>

            <div className="d-flex align-items-center gap-1">
                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onPrev}
                    disabled={loading || pageNo <= 1}
                    type="button"
                >
                    <FaChevronLeft />
                </button>

                <select
                    className="form-select form-select-sm mx-1"
                    style={{ width: 'auto' }}
                    value={pageNo}
                    onChange={(e) => onJump(Number(e.target.value))}
                    disabled={loading || totalPages <= 1}
                >
                    {pages.map((p) => (
                        <option key={p} value={p}>
                            {p}
                        </option>
                    ))}
                </select>

                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onNext}
                    disabled={loading || pageNo >= totalPages}
                    type="button"
                >
                    <FaChevronRight />
                </button>
            </div>
        </div>
    );
}

/* ---------- Confirmation Modal Component ---------- */
function ConfirmationModal({ show, type, withdrawalData, onClose, onConfirm }) {
    const [rejectReason, setRejectReason] = useState("");

    useEffect(() => {
        if (show) {
            setRejectReason("");
            // Prevent body scrolling when modal is open
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [show]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && show) {
                onClose();
            }
        };

        if (show) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [show, onClose]);

    if (!show) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleConfirm = () => {
        if (type === "reject" && !rejectReason.trim()) {
            toast.error("Please enter a reject reason");
            return;
        }
        onConfirm(type === "reject" ? rejectReason : null);
    };

    const title = type === "success" ? "Confirm Mark as Success" : "Confirm Reject Withdrawal";
    const confirmText = type === "success" ? "Mark as Success" : "Confirm Rejection";
    const confirmColor = type === "success" ? "btn-success" : "btn-danger";
    const icon = type === "success" ? <FaCheckCircle className="me-2" /> : <FaTimesCircle className="me-2" />;

    return (
        <>
            {/* Modal Backdrop */}
            <div
                className="modal-backdrop fade show"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1060,
                }}
                onClick={handleBackdropClick}
            />

            {/* Modal */}
            <div
                className="modal fade show"
                style={{
                    display: 'block',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1070,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                }}
                tabIndex="-1"
                role="dialog"
                onClick={handleBackdropClick}
            >
                <div
                    className="modal-dialog modal-dialog-centered"
                    style={{
                        maxWidth: '550px',
                        margin: '1.75rem auto',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content border-0 shadow">
                        <div className="modal-header bg-light">
                            <h5 className="modal-title mb-0">
                                {title}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Close"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="modal-body">
                            {type === "success" ? (
                                <div className="text-center">
                                    <FaCheckCircle className="text-success mb-3" size={48} />
                                    <h5 className="mb-3">Are you sure you want to mark this withdrawal as success?</h5>
                                    <div className="text-start">
                                        <p className="mb-1">
                                            <strong>Requested Amount:</strong> <span className="text-success">{nfmt(withdrawalData?.amount)}</span>
                                        </p>
                                        <p className="mb-1">
                                            <strong>Charge:</strong> <span className="text-danger">{nfmt(withdrawalData?.charge)}</span>
                                        </p>
                                        <p className="mb-1">
                                            <strong>Net Amount:</strong> <span className="text-success fw-bold">{nfmt(withdrawalData?.net)}</span>
                                        </p>
                                        <p className="mb-1">
                                            <strong>User:</strong> {withdrawalData?.userDisplay}
                                        </p>
                                        <p className="mb-0">
                                            <strong>Wallet:</strong> {withdrawalData?.walletType} ({withdrawalData?.walletAddress})
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className="text-center mb-3">
                                        <FaTimesCircle className="text-danger mb-2" size={48} />
                                        <h5>Are you sure you want to reject this withdrawal?</h5>
                                    </div>
                                    <div className="mb-3">
                                        <div className="text-start">
                                            <p className="mb-1">
                                                <strong>Requested Amount:</strong> <span className="text-success">{nfmt(withdrawalData?.amount)}</span>
                                            </p>
                                            <p className="mb-1">
                                                <strong>Charge:</strong> <span className="text-danger">{nfmt(withdrawalData?.charge)}</span>
                                            </p>
                                            <p className="mb-1">
                                                <strong>Net Amount:</strong> <span className="text-success fw-bold">{nfmt(withdrawalData?.net)}</span>
                                            </p>
                                            <p className="mb-1">
                                                <strong>User:</strong> {withdrawalData?.userDisplay}
                                            </p>
                                            <p className="mb-3">
                                                <strong>Wallet:</strong> {withdrawalData?.walletType} ({withdrawalData?.walletAddress})
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">
                                            Reject Reason <span className="text-danger">*</span>
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows={3}
                                            placeholder="Enter reason for rejection..."
                                            value={rejectReason}
                                            onChange={(e) => setRejectReason(e.target.value)}
                                            required
                                            autoFocus
                                        />
                                        <div className="form-text">Please provide a clear reason for rejecting this withdrawal.</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={`btn ${confirmColor}`}
                                onClick={handleConfirm}
                                disabled={type === "reject" && !rejectReason.trim()}
                            >
                                {icon}
                                {confirmText}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

/* ---------- Custom Modal Component ---------- */
function DetailsModal({ show, item, onClose, onMarkSuccess, onReject }) {
    useEffect(() => {
        if (show) {
            // Prevent body scrolling when modal is open
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [show]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && show) {
                onClose();
            }
        };

        if (show) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [show, onClose]);

    if (!show || !item) return null;

    const w = item?.withdrawl || {};
    const u = item?.user || {};
    const wallet = w?.wallet || {};

    const mobileDisplay = safeText(u?.mobile) === "—"
        ? "—"
        : `+${safeText(u?.country_code) === "—" ? "" : safeText(u?.country_code)} ${safeText(u?.mobile)}`.trim();

    const canAct = w?.status === "0";

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <>
            {/* Modal Backdrop */}
            <div
                className="modal-backdrop fade show"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1040,
                }}
                onClick={handleBackdropClick}
            />

            {/* Modal */}
            <div
                className="modal fade show"
                style={{
                    display: 'block',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1050,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                }}
                tabIndex="-1"
                role="dialog"
                onClick={handleBackdropClick}
            >
                <div
                    className="modal-dialog modal-lg modal-dialog-centered"
                    style={{
                        maxWidth: '900px',
                        margin: '1.75rem auto',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content border-0 shadow">
                        <div className="modal-header bg-light">
                            <div className="d-flex align-items-center">
                                <h5 className="modal-title mb-0">
                                    Withdrawal Details
                                </h5>
                                <span className="ms-2">
                                    <StatusBadge status={w?.status} />
                                </span>
                            </div>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Close"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="row g-3">
                                {/* Withdrawal Information */}
                                <div className="col-md-6">
                                    <div className="card h-100 border">
                                        <div className="card-header bg-white border-bottom">
                                            <h6 className="mb-0 fw-semibold">Withdrawal Information</h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="row mb-3">
                                                <div className="col-4">
                                                    <div className="text-muted small mb-1">Requested</div>
                                                    <div className="fw-bold fs-5 text-success">{nfmt(w?.amount?.amount)}</div>
                                                </div>
                                                <div className="col-4">
                                                    <div className="text-muted small mb-1">Charge</div>
                                                    <div className="fw-bold text-danger">{nfmt(w?.amount?.charge)}</div>
                                                </div>
                                                <div className="col-4">
                                                    <div className="text-muted small mb-1">Net Amount</div>
                                                    <div className="fw-bold text-success">{nfmt(w?.amount?.net)}</div>
                                                </div>
                                            </div>

                                            <div className="row mb-3">
                                                <div className="col-6">
                                                    <div className="text-muted small mb-1">Created</div>
                                                    <div>{formatDate(w?.create_date)}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-muted small mb-1">Status</div>
                                                    <div>
                                                        <StatusBadge status={w?.status} />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <div className="text-muted small mb-1">Withdrawal ID</div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <code className="text-truncate">{safeText(w?.withdrawl_id)}</code>
                                                    <CopyBtn value={w?.withdrawl_id} label="Withdrawal ID copied" />
                                                </div>
                                            </div>

                                            {w?.remark && (
                                                <div className="mb-2">
                                                    <div className="text-muted small mb-1">Remark</div>
                                                    <div className="fst-italic bg-light p-2 rounded">{safeText(w?.remark)}</div>
                                                </div>
                                            )}

                                            {w?.status === "2" && w?.reject_reason && (
                                                <div className="mb-2">
                                                    <div className="text-muted small mb-1">Reject Reason</div>
                                                    <div className="text-danger bg-light p-2 rounded">{safeText(w?.reject_reason)}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* User & Wallet Information */}
                                <div className="col-md-6">
                                    <div className="card h-100 border">
                                        <div className="card-header bg-white border-bottom">
                                            <h6 className="mb-0 fw-semibold">User & Wallet Information</h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="row mb-3">
                                                <div className="col-12 mb-3">
                                                    <div className="text-muted small mb-1">Name</div>
                                                    <div className="fw-semibold">{safeText(u?.name)}</div>
                                                </div>
                                                <div className="col-12 mb-3">
                                                    <div className="text-muted small mb-1">Mobile</div>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="fw-semibold">{mobileDisplay}</span>
                                                        <CopyBtn value={u?.mobile} label="Mobile copied" disabled={!u?.mobile} />
                                                    </div>
                                                </div>
                                                <div className="col-12 mb-3">
                                                    <div className="text-muted small mb-1">Email</div>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="text-truncate">{safeText(u?.email)}</span>
                                                        <CopyBtn value={u?.email} label="Email copied" disabled={!u?.email} />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-4 pt-3 border-top">
                                                <div className="text-muted small mb-1 d-flex align-items-center gap-2">
                                                    <FaWallet /> Wallet Details
                                                </div>
                                                <div className="bg-light p-3 rounded">
                                                    <div className="row mb-2">
                                                        <div className="col-6">
                                                            <div className="text-muted small mb-1">Wallet Type</div>
                                                            <div className="fw-bold text-uppercase">{safeText(wallet?.wallet_type)}</div>
                                                        </div>
                                                        <div className="col-6">
                                                            <div className="text-muted small mb-1">Wallet Address</div>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div
                                                                    className="text-truncate"
                                                                    style={{ maxWidth: "180px" }}
                                                                    title={safeText(wallet?.wallet_address)}
                                                                >
                                                                    {safeText(wallet?.wallet_address)}
                                                                </div>
                                                                <CopyBtn value={wallet?.wallet_address} label="Wallet address copied" disabled={!wallet?.wallet_address} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Actions for Pending Withdrawals */}
                            {canAct && (
                                <div className="card mt-3 border">
                                    <div className="card-header bg-white border-bottom">
                                        <h6 className="mb-0 fw-semibold">Actions</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="d-flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-success d-flex align-items-center"
                                                onClick={() => {
                                                    onMarkSuccess(w?.withdrawl_id, w?.amount?.amount, w?.amount?.charge, w?.amount?.net, u, wallet);
                                                }}
                                            >
                                                <FaCheckCircle className="me-2" />
                                                Mark as Success
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger d-flex align-items-center"
                                                onClick={() => {
                                                    onReject(w?.withdrawl_id, w?.amount?.amount, w?.amount?.charge, w?.amount?.net, u, wallet);
                                                }}
                                            >
                                                <FaTimesCircle className="me-2" />
                                                Reject Withdrawal
                                            </button>
                                            <div className="text-muted small align-self-center ms-2">
                                                Confirmation will be required before proceeding.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer bg-light">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

/* ============================== MAIN PAGE ============================== */
export default function WithdrawlList() {
    const [loading, setLoading] = useState(true);
    const [inputText, setInputText] = useState("");
    const [appliedQuery, setAppliedQuery] = useState("");
    const [tab, setTab] = useState("all");
    const [pageNo, setPageNo] = useState(1);
    const [list, setList] = useState([]);
    const [info, setInfo] = useState({ total_user: 0, page_no: 1 });
    const [selectedItem, setSelectedItem] = useState(null);
    const [confirmationModal, setConfirmationModal] = useState({
        show: false,
        type: null, // "success" or "reject"
        withdrawlId: null,
        withdrawalData: null
    });

    const navigate = useNavigate();

    const PAGE_SIZE = 20;

    const totalPages = useMemo(() => {
        const total = Number(info?.total_user || 0);
        if (!total) return 1;
        return Math.max(1, Math.ceil(total / PAGE_SIZE));
    }, [info?.total_user]);

    const statusParam = useMemo(() => {
        if (tab === "pending") return "0";
        if (tab === "success") return "1";
        if (tab === "rejected") return "2";
        return "";
    }, [tab]);

    const fetchWithdrawals = useCallback(
        async ({ page, query, status }) => {
            setLoading(true);
            try {
                const res = await axios({
                    method: "post",
                    url: `${BASE_ENDPOINT}/admin/withdrawl-list`,
                    headers: { "Content-Type": "application/json" },
                    data: {
                        page_no: page,
                        query: query || "",
                        status,
                        token: localStorage.getItem("token") || "",
                    },
                });

                const data = res?.data;

                if (data?.error) {
                    toast.error(typeof data?.error === "string" ? data.error : "Failed to load withdrawals");
                    setList([]);
                    setInfo({ total_user: 0, page_no: page });
                    return;
                }

                const raw = Array.isArray(data?.withdrawl_list) ? data.withdrawl_list : [];
                setList(raw);

                const apiInfo = data?.info || {};
                setInfo({
                    total_user: Number(apiInfo?.total_user || 0),
                    page_no: Number(apiInfo?.page_no || page),
                });
            } catch (err) {
                console.log(err);
                toast.error("Failed to load withdrawals");
                setList([]);
                setInfo({ total_user: 0, page_no: page });
            } finally {
                setLoading(false);
            }
        },
        []
    );

    // Initial load
    useEffect(() => {
        fetchWithdrawals({ page: 1, query: "", status: "" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refetch when tab/page/appliedQuery changes
    useEffect(() => {
        fetchWithdrawals({ page: pageNo, query: appliedQuery, status: statusParam });
    }, [pageNo, appliedQuery, statusParam, fetchWithdrawals]);

    const onSearch = () => {
        setPageNo(1);
        setAppliedQuery(inputText.trim());
    };

    const onRefreshReset = async () => {
        setInputText("");
        setAppliedQuery("");
        setPageNo(1);
        await fetchWithdrawals({ page: 1, query: "", status: statusParam });
    };

    const onPrev = () => setPageNo((p) => Math.max(1, p - 1));
    const onNext = () => setPageNo((p) => Math.min(totalPages, p + 1));
    const onJump = (p) => setPageNo(Math.min(totalPages, Math.max(1, p)));

    const openDetails = (item) => {
        setSelectedItem(item);
    };

    const closeDetails = () => {
        setSelectedItem(null);
    };

    const updateWithdrawalStatus = async (withdrawl_id, status, reject_reason = null) => {
        if (!withdrawl_id) return toast.error("Withdrawal ID missing");

        const payload = {
            withdrawl_id,
            status,
            token: localStorage.getItem("token") || "",
        };

        if (status === "2" && reject_reason) {
            payload.reject_reason = reject_reason;
        }

        try {
            const res = await axios.post(`${BASE_ENDPOINT}/admin/update-withdrawl`, payload);
            const data = res?.data;

            if (data?.msg) {
                toast.success(data.msg);
                // Refresh the current list
                fetchWithdrawals({ page: pageNo, query: appliedQuery, status: statusParam });
            } else if (data?.error) {
                toast.error(data.error);
            } else {
                toast.success("Withdrawal status updated successfully");
                fetchWithdrawals({ page: pageNo, query: appliedQuery, status: statusParam });
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to update withdrawal status");
        }
    };

    const openConfirmationModal = (type, withdrawlId, withdrawalData) => {
        setConfirmationModal({
            show: true,
            type,
            withdrawlId,
            withdrawalData
        });
    };

    const closeConfirmationModal = () => {
        setConfirmationModal({
            show: false,
            type: null,
            withdrawlId: null,
            withdrawalData: null
        });
    };

    const handleConfirmAction = async (rejectReason = null) => {
        if (confirmationModal.type === "success") {
            await updateWithdrawalStatus(confirmationModal.withdrawlId, "1");
        } else if (confirmationModal.type === "reject") {
            if (!rejectReason || !rejectReason.trim()) {
                toast.error("Reject reason is required");
                return;
            }
            await updateWithdrawalStatus(confirmationModal.withdrawlId, "2", rejectReason);
        }
        // Close both confirmation modal and details modal
        closeConfirmationModal();
        closeDetails();
    };

    const handleMarkSuccess = (withdrawl_id, amount, charge, net, user, wallet) => {
        const userDisplay = getUserDisplay(user);
        openConfirmationModal("success", withdrawl_id, {
            amount,
            charge,
            net,
            userDisplay,
            walletType: wallet?.wallet_type,
            walletAddress: wallet?.wallet_address
        });
    };

    const handleReject = (withdrawl_id, amount, charge, net, user, wallet) => {
        const userDisplay = getUserDisplay(user);
        openConfirmationModal("reject", withdrawl_id, {
            amount,
            charge,
            net,
            userDisplay,
            walletType: wallet?.wallet_type,
            walletAddress: wallet?.wallet_address
        });
    };

    // Format user display (name only)
    const getUserDisplay = (user) => {
        if (!user) return "—";
        const name = safeText(user.name);
        const mobile = safeText(user.mobile);
        const countryCode = safeText(user.country_code);

        const mobileDisplay = mobile !== "—"
            ? `+${countryCode !== "—" ? countryCode : ""} ${mobile}`.trim()
            : "";

        if (name !== "—") {
            return name;
        } else if (mobileDisplay) {
            return mobileDisplay;
        }
        return "—";
    };

    // Get user mobile for tooltip/extra info
    const getUserMobile = (user) => {
        if (!user) return "";
        const mobile = safeText(user.mobile);
        const countryCode = safeText(user.country_code);

        if (mobile !== "—") {
            return `+${countryCode !== "—" ? countryCode : ""} ${mobile}`.trim();
        }
        return "";
    };

    return (
        <PageTransition>
            <div className="container-fluid py-3">
                {/* Header */}
                <div className="row mb-3">
                    <div className="col">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h1 className="h3 mb-1">Withdrawal Management</h1>
                                <p className="text-muted mb-0">
                                    Total: <strong>{nfmt(info.total_user, 0)}</strong>
                                    {appliedQuery && (
                                        <span className="ms-2">
                                            • Search: <strong>{appliedQuery}</strong>
                                        </span>
                                    )}
                                </p>
                            </div>
                            <button
                                className="btn btn-outline-primary"
                                onClick={onRefreshReset}
                                disabled={loading}
                            >
                                <FaSyncAlt className={`me-2 ${loading ? 'spin' : ''}`} />
                                {loading ? "Refreshing..." : "Refresh"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="row mb-3">
                    <div className="col">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <ul className="nav nav-tabs border-0">
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${tab === "all" ? "active" : ""}`}
                                                onClick={() => {
                                                    setTab("all");
                                                    setPageNo(1);
                                                }}
                                            >
                                                <FaFilter className="me-2" />
                                                All
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${tab === "pending" ? "active" : ""}`}
                                                onClick={() => {
                                                    setTab("pending");
                                                    setPageNo(1);
                                                }}
                                            >
                                                <FaClock className="me-2" />
                                                Pending
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${tab === "success" ? "active" : ""}`}
                                                onClick={() => {
                                                    setTab("success");
                                                    setPageNo(1);
                                                }}
                                            >
                                                <FaCheckCircle className="me-2" />
                                                Success
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${tab === "rejected" ? "active" : ""}`}
                                                onClick={() => {
                                                    setTab("rejected");
                                                    setPageNo(1);
                                                }}
                                            >
                                                <FaTimesCircle className="me-2" />
                                                Rejected
                                            </button>
                                        </li>
                                    </ul>

                                    <div className="d-flex align-items-center gap-2">
                                        <div className="input-group input-group-sm" style={{ width: "300px" }}>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search by user name, mobile, wallet address..."
                                                value={inputText}
                                                onChange={(e) => setInputText(e.target.value)}
                                                onKeyDown={(e) => e.key === "Enter" && onSearch()}
                                            />
                                            <button
                                                className="btn btn-primary"
                                                type="button"
                                                onClick={onSearch}
                                                disabled={loading}
                                            >
                                                <FaSearch />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Table */}
                <div className="row">
                    <div className="col">
                        <div className="card">
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: "15%" }}>Created Date</th>
                                                <th style={{ width: "20%" }}>User</th>
                                                <th style={{ width: "10%" }}>Amount</th>
                                                <th style={{ width: "5%" }}>Charge</th>
                                                <th style={{ width: "10%" }}>Net</th>
                                                <th style={{ width: "15%" }}>Wallet</th>
                                                <th style={{ width: "10%" }}>Status</th>
                                                <th style={{ width: "5%" }}></th>
                                                <th style={{ width: "5%" }}></th>
                                                {tab === "pending" && <th style={{ width: "5%" }}></th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <>
                                                    {[...Array(5)].map((_, i) => (
                                                        <LoadingRow key={i} cols={tab === "pending" ? 8 : 7} />
                                                    ))}
                                                </>
                                            ) : list.length === 0 ? (
                                                <tr>
                                                    <td colSpan={tab === "pending" ? 10 : 9} className="text-center py-5">
                                                        <div className="text-muted">
                                                            <FaSearch className="mb-2" size={24} />
                                                            <p className="mb-0">No withdrawals found</p>
                                                            {appliedQuery && (
                                                                <small>Try changing your search query</small>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                list.map((row) => {
                                                    const w = row?.withdrawl || {};
                                                    const u = row?.user || {};
                                                    const wallet = w?.wallet || {};
                                                    const userMobile = getUserMobile(u);
                                                    const userDisplay = getUserDisplay(u);

                                                    return (
                                                        <tr key={w?.withdrawl_id}>
                                                            <td>
                                                                <div className="fw-medium">{formatDateShort(w?.create_date)}</div>
                                                                <small className="text-muted">{formatDate(w?.create_date).split(' ')[1]}</small>
                                                            </td>
                                                            <td>
                                                                <div>{userDisplay}</div>
                                                                {userMobile && userDisplay !== userMobile && (
                                                                    <small className="text-muted">{userMobile}</small>
                                                                )}
                                                            </td>
                                                            <td className="fw-bold">{nfmt(w?.amount?.amount)}</td>
                                                            <td className="text-danger">{nfmt(w?.amount?.charge)}</td>
                                                            <td className="text-success fw-bold">{nfmt(w?.amount?.net)}</td>
                                                            <td>
                                                                <div>
                                                                    <div className="fw-semibold text-uppercase small">{safeText(wallet?.wallet_type)}</div>
                                                                    <div className="text-truncate small" style={{ maxWidth: "120px" }} title={safeText(wallet?.wallet_address)}>
                                                                        {safeText(wallet?.wallet_address)}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <StatusBadge status={w?.status} />
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-outline-info"
                                                                    onClick={() => openDetails(row)}
                                                                    title="View Details"
                                                                >
                                                                    <FaInfoCircle />
                                                                </button>
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-outline-info"
                                                                    onClick={() => {
                                                                        window.open(`../user-profile/${u.username}`, "_blank", "noopener,noreferrer");
                                                                    }}
                                                                    title="View Profile"
                                                                >
                                                                    <CgProfile />
                                                                </button>
                                                            </td>
                                                            {
                                                                tab === "pending" && (
                                                                    <td>
                                                                        <div className="dropdown">
                                                                            <button
                                                                                className="btn btn-sm btn-outline-primary dropdown-toggle"
                                                                                type="button"
                                                                                data-bs-toggle="dropdown"
                                                                                aria-expanded="false"
                                                                            >
                                                                                <FaEllipsisV />
                                                                            </button>
                                                                            <ul className="dropdown-menu dropdown-menu-end">
                                                                                <li>
                                                                                    <button
                                                                                        className="dropdown-item text-success"
                                                                                        onClick={() => handleMarkSuccess(w?.withdrawl_id, w?.amount?.amount, w?.amount?.charge, w?.amount?.net, u, wallet)}
                                                                                    >
                                                                                        <FaCheckCircle className="me-2" />
                                                                                        Mark Success
                                                                                    </button>
                                                                                </li>
                                                                                <li>
                                                                                    <button
                                                                                        className="dropdown-item text-danger"
                                                                                        onClick={() => handleReject(w?.withdrawl_id, w?.amount?.amount, w?.amount?.charge, w?.amount?.net, u, wallet)}
                                                                                    >
                                                                                        <FaTimesCircle className="me-2" />
                                                                                        Reject
                                                                                    </button>
                                                                                </li>
                                                                            </ul>
                                                                        </div>
                                                                    </td>
                                                                )
                                                            }
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {!loading && list.length > 0 && (
                                    <div className="card-footer">
                                        <PaginationBar
                                            loading={loading}
                                            pageNo={pageNo}
                                            totalPages={totalPages}
                                            onPrev={onPrev}
                                            onNext={onNext}
                                            onJump={onJump}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Details Modal */}
                <DetailsModal
                    show={!!selectedItem}
                    item={selectedItem}
                    onClose={closeDetails}
                    onMarkSuccess={handleMarkSuccess}
                    onReject={handleReject}
                />

                {/* Confirmation Modal */}
                <ConfirmationModal
                    show={confirmationModal.show}
                    type={confirmationModal.type}
                    withdrawalData={confirmationModal.withdrawalData}
                    onClose={closeConfirmationModal}
                    onConfirm={handleConfirmAction}
                />

                {/* Custom CSS */}
                <style jsx>{`
                    .nav-tabs .nav-link {
                        border: none;
                        color: #6c757d;
                        padding: 0.5rem 1rem;
                    }
                    .nav-tabs .nav-link.active {
                        color: #0d6efd;
                        border-bottom: 2px solid #0d6efd;
                        background: transparent;
                    }
                    .nav-tabs .nav-link:hover:not(.active) {
                        color: #495057;
                    }
                    .spin {
                        animation: spin 1s linear infinite;
                    }
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    .table th {
                        font-weight: 600;
                        font-size: 0.875rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }
                    .table td {
                        vertical-align: middle;
                        font-size: 0.9rem;
                    }
                    .modal-backdrop {
                        z-index: 1040 !important;
                    }
                    .modal {
                        z-index: 1050 !important;
                    }
                    .modal-dialog {
                        animation: modalSlideIn 0.3s ease-out;
                    }
                    @keyframes modalSlideIn {
                        from {
                            opacity: 0;
                            transform: translateY(-20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    .btn-close {
                        background: transparent;
                        border: none;
                        font-size: 1.2rem;
                        opacity: 0.5;
                        transition: opacity 0.2s;
                    }
                    .btn-close:hover {
                        opacity: 0.75;
                    }
                `}</style>
            </div>
        </PageTransition >
    );
}